/**
 * ChangePasswordDialog - Modal para alteração de senha do usuário
 * 
 * Permite ao usuário alterar sua senha com validação de força
 * e confirmação de senha. Suporta i18n para múltiplos idiomas.
 * 
 * @author Sistema
 * @version 1.1.0
 */

import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, KeyRound, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Regex para caractere especial
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=\[\]{}|;':",.<>?\/\\`~]/;

type ChangePasswordFormData = {
  newPassword: string;
  confirmPassword: string;
};

/**
 * Calcula a força da senha (0-100)
 */
const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 10;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[a-z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (SPECIAL_CHAR_REGEX.test(password)) strength += 20;
  
  return Math.min(100, strength);
};

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Schema de validação com mensagens traduzidas
  const changePasswordSchema = useMemo(() => z.object({
    newPassword: z.string()
      .min(8, t('auth:changePassword.requirements.minLength'))
      .regex(/[A-Z]/, t('auth:changePassword.requirements.uppercase'))
      .regex(/[a-z]/, t('auth:changePassword.requirements.lowercase'))
      .regex(/[0-9]/, t('auth:changePassword.requirements.number'))
      .regex(SPECIAL_CHAR_REGEX, t('auth:changePassword.requirements.special')),
    confirmPassword: z.string(),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('auth:changePassword.errors.passwordMismatch'),
    path: ['confirmPassword'],
  }), [t]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  const newPassword = watch('newPassword', '');
  const passwordStrength = calculatePasswordStrength(newPassword);

  /**
   * Retorna a cor e label da força da senha
   */
  const getStrengthInfo = (strength: number): { color: string; label: string } => {
    if (strength < 30) return { color: 'bg-destructive', label: t('auth:changePassword.strengthLevels.veryWeak') };
    if (strength < 50) return { color: 'bg-orange-500', label: t('auth:changePassword.strengthLevels.weak') };
    if (strength < 70) return { color: 'bg-yellow-500', label: t('auth:changePassword.strengthLevels.medium') };
    if (strength < 90) return { color: 'bg-lime-500', label: t('auth:changePassword.strengthLevels.strong') };
    return { color: 'bg-green-500', label: t('auth:changePassword.strengthLevels.veryStrong') };
  };

  const strengthInfo = getStrengthInfo(passwordStrength);

  // Requisitos de senha (5 requisitos incluindo caractere especial)
  const requirements = [
    { label: t('auth:changePassword.requirements.minLength'), met: newPassword.length >= 8 },
    { label: t('auth:changePassword.requirements.uppercase'), met: /[A-Z]/.test(newPassword) },
    { label: t('auth:changePassword.requirements.lowercase'), met: /[a-z]/.test(newPassword) },
    { label: t('auth:changePassword.requirements.number'), met: /[0-9]/.test(newPassword) },
    { label: t('auth:changePassword.requirements.special'), met: SPECIAL_CHAR_REGEX.test(newPassword) },
  ];

  /**
   * Handler de submit do formulário
   */
  const onSubmit = async (data: ChangePasswordFormData) => {
    setIsSubmitting(true);
    
    try {
      const { error } = await updatePassword(data.newPassword);
      
      if (error) {
        // Detecta tipo de erro do Supabase para exibir mensagem apropriada
        const errorMessage = error.message?.toLowerCase() || '';
        
        // Erro: senha igual à atual
        const isSamePasswordError = 
          errorMessage.includes('same password') ||
          errorMessage.includes('different from the old') ||
          errorMessage.includes('should be different');
        
        // Erro: requisitos de senha não atendidos
        const isPasswordRequirementsError = 
          errorMessage.includes('password should contain') || 
          errorMessage.includes('password must contain') ||
          errorMessage.includes('character of each') ||
          errorMessage.includes('special character');
        
        // Seleciona mensagem traduzida apropriada
        let errorDescription: string;
        if (isSamePasswordError) {
          errorDescription = t('auth:changePassword.errors.samePassword');
        } else if (isPasswordRequirementsError) {
          errorDescription = t('auth:changePassword.errors.requirementsNotMet');
        } else {
          errorDescription = t('auth:changePassword.errors.generic');
        }
        
        toast.error(t('auth:changePassword.errors.title'), {
          description: errorDescription,
        });
        return;
      }
      
      toast.success(t('auth:changePassword.success.title'), {
        description: t('auth:changePassword.success.description'),
      });
      
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(t('auth:changePassword.errors.title'), {
        description: t('auth:changePassword.errors.unexpected'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Limpa o formulário ao fechar o modal
   */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
              <KeyRound className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>{t('auth:changePassword.title')}</DialogTitle>
              <DialogDescription>
                {t('auth:changePassword.subtitle')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 mt-4">
          {/* Nova Senha */}
          <div className="space-y-2">
            <Label htmlFor="newPassword">{t('auth:changePassword.newPassword')}</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth:changePassword.newPasswordPlaceholder')}
                {...register('newPassword')}
                className={cn(
                  'pr-10',
                  errors.newPassword && 'border-destructive focus-visible:ring-destructive'
                )}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            
            {/* Indicador de força */}
            {newPassword && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{t('auth:changePassword.strengthLabel')}</span>
                  <span className={cn(
                    'font-medium',
                    passwordStrength < 50 ? 'text-destructive' : 
                    passwordStrength < 70 ? 'text-yellow-600' : 
                    'text-green-600'
                  )}>
                    {strengthInfo.label}
                  </span>
                </div>
                <Progress 
                  value={passwordStrength} 
                  className="h-1.5"
                  indicatorClassName={strengthInfo.color}
                />
              </div>
            )}
            
            {errors.newPassword && (
              <p className="text-sm text-destructive">{errors.newPassword.message}</p>
            )}
          </div>

          {/* Requisitos de senha - 5 requisitos incluindo caractere especial */}
          {newPassword && (
            <div className="grid grid-cols-2 gap-2">
              {requirements.map((req, index) => (
                <div 
                  key={index} 
                  className={cn(
                    'flex items-center gap-1.5 text-xs',
                    req.met ? 'text-green-600' : 'text-muted-foreground'
                  )}
                >
                  {req.met ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                  <span>{req.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirmar Senha */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('auth:changePassword.confirmPassword')}</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('auth:changePassword.confirmPasswordPlaceholder')}
                {...register('confirmPassword')}
                className={cn(
                  'pr-10',
                  errors.confirmPassword && 'border-destructive focus-visible:ring-destructive'
                )}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('auth:changePassword.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('auth:changePassword.submitting')}
                </>
              ) : (
                t('auth:changePassword.submit')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
